import React, { useCallback, useEffect, useState } from "react";
import type { CustomerMonthlyReport, MonthlyReportId, SiteId } from "@siteagain/shared-types";

import { useLocale, type Locale } from "./i18n";
import { MonthlyReportView, ReportDialog } from "./monthly-report";
import { formatMonth, operatorFetch } from "./operator/format";

/**
 * The customer's side of the monthly report: a list of the approved reports for one site, each
 * opening in the same view the operator reviewed, and the dialog an emailed link lands on.
 * Drafts and withheld reports never reach this code: the API does not return them.
 */
const EN = {
  eyebrow: "Monthly report",
  title: "What was done for your website",
  subtitle: "One report per calendar month, written from the records of what was published.",
  empty: "Your first monthly report is written after a full calendar month.",
  loadFailed: "Could not load your monthly reports.",
  open: "Read",
  quiet: "Quiet month: nothing was published.",
  line: (shipped: number, rolledBack: number, measured: number) => {
    const parts: string[] = [];
    if (shipped > 0) parts.push(`${shipped} ${shipped === 1 ? "change" : "changes"} live`);
    if (rolledBack > 0) parts.push(`${rolledBack} rolled back`);
    if (measured > 0) parts.push(`${measured} measured`);
    return parts.join(" · ");
  },
  close: "Close report",
  linkFailed: "This report is not available. It may belong to another account, or it may not have been published yet.",
  loading: "Loading your report…",
  dialog: (month: string) => `Monthly report, ${month}`,
};

const FR: typeof EN = {
  eyebrow: "Rapport mensuel",
  title: "Ce qui a été fait pour votre site",
  subtitle: "Un rapport par mois civil, écrit à partir des enregistrements de ce qui a été publié.",
  empty: "Votre premier rapport mensuel est écrit après un mois civil complet.",
  loadFailed: "Impossible de charger vos rapports mensuels.",
  open: "Lire",
  quiet: "Mois calme : rien n'a été publié.",
  line: (shipped, rolledBack, measured) => {
    const parts: string[] = [];
    if (shipped > 0) parts.push(`${shipped} ${shipped === 1 ? "modification en ligne" : "modifications en ligne"}`);
    if (rolledBack > 0) parts.push(`${rolledBack} ${rolledBack === 1 ? "annulée" : "annulées"}`);
    if (measured > 0) parts.push(`${measured} ${measured === 1 ? "mesurée" : "mesurées"}`);
    return parts.join(" · ");
  },
  close: "Fermer le rapport",
  linkFailed: "Ce rapport n'est pas disponible. Il appartient peut-être à un autre compte, ou n'a pas encore été publié.",
  loading: "Chargement de votre rapport…",
  dialog: (month) => `Rapport mensuel, ${month}`,
};

const copyFor = (locale: Locale) => (locale === "fr" ? FR : EN);

export function MonthlyReportsSection({ siteId }: { siteId: SiteId }) {
  const locale = useLocale();
  const t = copyFor(locale);
  const [reports, setReports] = useState<CustomerMonthlyReport[] | null>(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState<MonthlyReportId | null>(null);

  useEffect(() => {
    let active = true;
    setReports(null);
    setError("");
    operatorFetch<{ reports: CustomerMonthlyReport[] }>(`/sites/${siteId}/monthly-reports`)
      .then((response) => { if (active) setReports(response.reports); })
      .catch(() => { if (active) setError(t.loadFailed); });
    return () => { active = false; };
  }, [siteId, t.loadFailed]);

  const open = reports?.find((item) => item.id === openId) ?? null;

  return (
    <section className="growth-section monthly-reports" aria-labelledby="monthly-reports-title">
      <div className="growth-section-heading">
        <div><small>{t.eyebrow}</small><h2 id="monthly-reports-title">{t.title}</h2></div>
      </div>
      <p className="monthly-reports-subtitle">{t.subtitle}</p>
      {error && <p className="monthly-reports-error" role="alert">{error}</p>}
      {reports !== null && reports.length === 0 && !error && <p className="monthly-reports-empty">{t.empty}</p>}
      {reports !== null && reports.length > 0 && (
        <ul className="monthly-reports-list">
          {reports.map((item) => {
            const line = item.quiet ? t.quiet : t.line(item.report.totals.shipped, item.report.totals.rolledBack, item.report.totals.measured);
            return (
              <li key={item.id}>
                <div>
                  <strong>{formatMonth(item.month, locale)}</strong>
                  <span>{line}</span>
                </div>
                <button type="button" onClick={() => setOpenId(item.id)}>{t.open}</button>
              </li>
            );
          })}
        </ul>
      )}
      {open && (
        <ReportDialog
          label={t.dialog(formatMonth(open.month, locale))}
          eyebrow={`${open.report.siteName} · ${formatMonth(open.month, locale)}`}
          closeLabel={t.close}
          onClose={() => setOpenId(null)}
        >
          <MonthlyReportView report={open.report} />
        </ReportDialog>
      )}
    </section>
  );
}

const STASH_KEY = "siteagain_monthly_report_link";
const REPORT_ID = /^mrp_[A-Za-z0-9_-]+$/u;

/**
 * Reads `?monthlyReport=<id>` from the email link once, removes it from the address bar, and keeps
 * it for the tab so it survives the sign-in round trip. Returns the pending id, if any.
 */
export function adoptMonthlyReportLink(): MonthlyReportId | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("monthlyReport");
    if (params.has("monthlyReport")) {
      params.delete("monthlyReport");
      const query = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    }
    if (fromUrl && REPORT_ID.test(fromUrl)) {
      try { sessionStorage.setItem(STASH_KEY, fromUrl); } catch {}
      return fromUrl as MonthlyReportId;
    }
  } catch {}
  try {
    const stored = sessionStorage.getItem(STASH_KEY);
    return stored && REPORT_ID.test(stored) ? stored as MonthlyReportId : null;
  } catch {
    return null;
  }
}

export function clearMonthlyReportLink(): void {
  try { sessionStorage.removeItem(STASH_KEY); } catch {}
}

/** The dialog an emailed link opens, once the viewer is signed in. */
export function MonthlyReportLinkDialog({ reportId, onClose }: { reportId: MonthlyReportId; onClose(): void }) {
  const locale = useLocale();
  const t = copyFor(locale);
  const [report, setReport] = useState<CustomerMonthlyReport | null>(null);
  const [error, setError] = useState("");

  const close = useCallback(() => { clearMonthlyReportLink(); onClose(); }, [onClose]);

  useEffect(() => {
    let active = true;
    operatorFetch<{ report: CustomerMonthlyReport }>(`/monthly-reports/${reportId}`)
      .then((response) => { if (active) setReport(response.report); })
      .catch(() => { if (active) setError(t.linkFailed); });
    return () => { active = false; };
  }, [reportId, t.linkFailed]);

  return (
    <ReportDialog
      label={report ? t.dialog(formatMonth(report.month, locale)) : t.title}
      eyebrow={report ? `${report.report.siteName} · ${formatMonth(report.month, locale)}` : t.eyebrow}
      closeLabel={t.close}
      onClose={close}
    >
      {error && <p className="monthly-reports-error" role="alert">{error}</p>}
      {!report && !error && <p className="monthly-reports-empty">{t.loading}</p>}
      {report && <MonthlyReportView report={report.report} />}
    </ReportDialog>
  );
}
